// This file contains all project data
export const projectsData = [
    {
      title: 'myold.chat',
      slug: 'myold-chat',
      date: 'October 2026',
      summary: "A browser-only reader for old iChat and Messages transcripts, where the files never leave the tab and the hardest problem turned out to be working out which of the messages were mine",
      description: `
        <p>There are things that already read old iChat transcripts. Past for iChat works fine. The problem is you get one file at a time, and ten years of talking to the same person is spread over dozens of them, in a binary format, usually on a Mac you don't have any more. I wanted one searchable thread per person that I could wind back to a given date.</p>

        <p>What it does with the files is the dull part. The point is that you end up looking at a list of people instead of a list of files. Everything one person said to you is a single thread, whichever account they were using and whichever of mine they reached, and it looks like Messages does, so you read it rather than operate it.</p>

        <p>Search runs across everyone at once and tells you there are four matches in three chats before you've opened anything. Each person gets a bar chart of months, so the stretch where you talked every day and the year you didn't are both obvious at a glance. And Go to date rebuilds the list as it was on the day you pick, with every chat opening at that day, which is about as close as I can get to opening the old Mac on a Tuesday in 2008.</p>

        <p>The one bit of engineering worth mentioning is that some Messages transcripts don't record your own name at all, just a flag bit, so the first version showed me half of about a dozen conversations. It's guesswork now, and when it guesses wrong there's a "Which are you?" setting to fix it. Nothing leaves the tab either. It's a static site and the Content-Security-Policy won't let the page make a network request at all.</p>
      `,
      featuredImage: require('@/assets/projects/myold-chat/featured.png'),
      externalLink: 'https://www.myold.chat',
      images: [
        {
          src: require('@/assets/projects/myold-chat/threads.png'),
          caption: 'One thread per person down the side, merged across every account they used. These are the made-up sample chats it ships with'
        },
        {
          src: require('@/assets/projects/myold-chat/search.png'),
          caption: 'Searching everyone at once. Four matches in three chats, marked in the thread as well as in the list'
        },
        {
          src: require('@/assets/projects/myold-chat/person.png'),
          caption: 'Sixty messages and ten chats between 2008 and 2010, stitched back together from one AIM screen name. Each bar is a month'
        },
        {
          src: require('@/assets/projects/myold-chat/go-to-date.png'),
          caption: 'Go to date rebuilds the chat list as it was on the day, and opens each chat at that day'
        },
        {
          src: require('@/assets/projects/myold-chat/which-are-you.png'),
          caption: 'The override for when it guesses wrong. Three accounts ticked as mine, across AIM, Jabber and iMessage'
        }
      ]
    },
    {
      title: '24-0 F1 Draft',
      slug: 'griddraft',
      date: 'October 2026',
      summary: 'Spin a historic F1 team-season, take one piece of it, do that five times, then race whatever you have cobbled together over a 24-round season',
      description: `
        <p>I regard myself as an above average consumer of F1 but the data gathering for this even surprised me. I ended up just getting Claude to rank it for me as there are simply too many teams and too much inheritance nonsense from brand to brand: Honda to Brawn to Merc etc.</p>

        <p>Like my other games, this is basically a rehash of something that exists. Again, it draws on fantasy draft games except now the twist is the tracks are modeled to reflect the strength (or otherwise) of elements of your team. Monza rewards engines, Monaco rewards the chassis, Spa rewards drivers and principals making strategy calls.</p>

        <p>The first person I sent this to was the pal I went to Montreal with in 2017 for the Grand Prix. He hated it.</p>
      `,
      featuredImage: require('@/assets/projects/griddraft/team.png'),
      externalLink: 'https://www.griddraft.fun',
      images: [
        {
          src: require('@/assets/projects/griddraft/draft.png'),
          caption: 'Round one. BAR 2004 comes up and you can have exactly one of them: Button, Sato, the Honda V10, the chassis or David Richards'
        },
        {
          src: require('@/assets/projects/griddraft/team.png'),
          caption: 'Five rounds later. A Honda V10, a 1980 Williams chassis, Button and Damon Hill, and Jackie Stewart running the place'
        },
        {
          src: require('@/assets/projects/griddraft/result.png'),
          caption: 'One win from twenty-four. The pit board shows every race, with Mexico in red'
        },
        {
          src: require('@/assets/projects/griddraft/season.png'),
          caption: 'Final standings. Ferrari 2004 won twenty-two of them, which is roughly what Ferrari 2004 did in real life'
        },
        {
          src: require('@/assets/projects/griddraft/distribution.png'),
          caption: 'The same build raced 200 times over. P5.54 on average, and not a single title in any of them'
        }
      ]
    },
    {
      title: 'Cò as motha?',
      slug: 'co-as-motha',
      date: 'September 2026',
      summary: "Scottish football's higher-or-lower: which of these two clubs has the better average league position since 1994-95?",
      description: `
        <p>Ever heard of 38-0-0? The Premier League draft game that had everyone's attention for about five minutes? Well why not try "Cò as motha?" The one-shot Claude Code project everyone* (*no one) is talking about!</p>

        <p>Every club that's played in Scotland's top four tiers since 1994-95 gets boiled down to one number: its average finishing position on a single ladder running from the top of the Premiership to the bottom of League Two. Celtic are on 1.41. East Stirlingshire are on 39.41. Two clubs, ten rounds, and the first wrong answer ends the run.</p>

        <p>The maths is the easy bit. The hard bit is deciding what a club is, and I'm from Clydebank, so I'm not coming at this neutrally. Clydebank were bought in 2002, moved to Airdrie and renamed, and the club playing there this afternoon is legally the one that used to play in my town. The data won't take sides. Clydebank's record just stops in 2002, a complete account of a thing that ended.</p>

        <p>Rangers get the same treatment because even though Rangers are definitely a new club and arguing anything else is totally legally mad, people still will argue it. And I can't be arsed with that. Every lineage call like that is written up on a How It Works page longer than the game itself.</p>
      `,
      featuredImage: require('@/assets/projects/co-as-motha/round.png'),
      externalLink: 'https://www.coasmotha.com',
      images: [
        {
          src: require('@/assets/projects/co-as-motha/home.png'),
          caption: 'The home screen, with the daily puzzle alongside the endless run and the Rangers died mode switch'
        },
        {
          src: require('@/assets/projects/co-as-motha/round.png'),
          caption: 'Albion Rovers against Clydebank, dagger and all. The dagger means the record stops'
        },
        {
          src: require('@/assets/projects/co-as-motha/result.png'),
          caption: 'Answering shows both averages, where each club sits out of 51, and how far apart they were'
        },
        {
          src: require('@/assets/projects/co-as-motha/run-over.png'),
          caption: 'Three out of ten, undone by Queen of the South against Clyde, which I would get wrong again'
        },
        {
          src: require('@/assets/projects/co-as-motha/how-it-works.png'),
          caption: 'Every judgement call is written up, from why we start at 1994-95 to why the Highland and Lowland Leagues cannot fairly be counted'
        }
      ]
    },
    {
      title: 'Necrotype',
      slug: 'necrotype',
      date: 'March 2026',
      summary: 'My attempt at rebuilding a Dreamcast typing game Anna played at a retro gaming night, where the keyboard is the only weapon you get',
      description: `
        <p>Anna played something like this on a Dreamcast at a local retro gaming night and I liked the idea enough to go home and have a go at rebuilding it. Zombies come down a pixelated corridor with a word above each one. Typing a word's first letter locks you onto that zombie and finishing the word kills it, and there's no mashing your way out of it, a wrong letter is just a wrong letter.</p>

        <p>There are no sound files in it. Every noise is made in the browser as it happens: oscillators for the typing and the kills, white noise pushed through filters for the hits, and a groan per zombie built out of a sawtooth down around 55Hz with a wobble on it. A corridor of ten zombies ends up sounding like ten separate things rather than the same clip ten times over.</p>

        <p>The dictionary took the most fiddling. It's six banks now, from three-letter words in wave one up to long two-word phrases later on, and each wave gets split into short, medium and long and then interleaved, because a wave where everything is the same shape is dull to type. It's still quite humbling. I got down to 66% health on wave one while testing it. More than once.</p>
      `,
      featuredImage: require('@/assets/projects/necrotype/gameplay.png'),
      externalLink: 'https://www.necrotype.fun',
      images: [
        {
          src: require('@/assets/projects/necrotype/title.png'),
          caption: 'Three lives and one rule, which is about all the instruction this kind of thing needs'
        },
        {
          src: require('@/assets/projects/necrotype/gameplay.png'),
          caption: 'Three zombies, three words. The one on the right is the problem'
        },
        {
          src: require('@/assets/projects/necrotype/game-over.png'),
          caption: 'The end of a run. Accuracy and best combo are the numbers that actually tell you how you did'
        }
      ]
    },
    {
      title: 'Cyfrifiannell Cymraeg',
      slug: 'cyfrifiannell-cymraeg',
      date: 'March 2026',
      summary: 'Using the Claude API to create a chatbot that finds a person in common with the user in imitation of the Cymru Connection on the Elis & John podcast',
      description: `
        <p>I've used Claude Code plenty, but this was my first time interacting with the API. I also used Vercel for the hosting of this light app as it requires essentially no backend making their free tier ideal.</p>

        <p>In the Cymru Connection, Elis James attempts to find a person in common with a caller to his podcast with John Robins. Think Six Degrees Of Separation but for Wales.</p>
        <p>My verion, Cyfrifiannell Cymraeg or "Welsh Calculator", tries to ask the same questions and similarly find a person the user might know. In line with the show, the idea is to find someone who is not just a famous Welsh person so the Claude API is instructed specifically to look at schools, the person's age, and not just to immediately go for Gareth Bale or Tom Jones</p>
        <p>Something I'm especially proud of is Elis Mode. Here, the AI has access to the previous transcripts of the podcast and therefore previous editions of Cymru Connection, allowing it to draw on Elis's network.</p>
      `,
      featuredImage: require('@/assets/projects/cyfrifiannell-cymraeg/hero.png'),
      externalLink: 'https://cyfrifiannell.cymru',
      images: [
        {
          src: require('@/assets/projects/cyfrifiannell-cymraeg/home.png'),
          caption: 'The home page shows previous success rates. IRLis has a roughly 50% hit rate'
        },
        {
          src: require('@/assets/projects/cyfrifiannell-cymraeg/elis-mode.png'),
          caption: 'Cyfrifiannell Cymraeg can get to 80% so sadly Elis Mode must come with a warning'
        },
        {
          src: require('@/assets/projects/cyfrifiannell-cymraeg/copying-the-show.png'),
          caption: 'Incredibly, given the same information, Cyfrifiannell Cymraeg made the same guess as Elis on the first show after its release'
        }
      ]
    },
    {
      title: 'Palette',
      slug: 'palette',
      date: 'September 2025',
      summary: 'An art marketplace that puts an emphasis on human creation - despite having been written with the help of Claude Code',
      description: `
        <p>Have you tried Etsy or Amazon lately? The amount of utter slop on offer is unbearable. In an AI driven world, getting human made products seems impossible. But for art, where curation is especially important, I think it's more possible.</p>

        <p>Features include:</p>
        <ul>
          <li>Stripe ID integration to ensure every seller is a real person</li>
          <li>Payment processing</li>
          <li>Comprehensive metadata on products to enable search</li>
        </ul>
      `,
      featuredImage: require('@/assets/projects/palette/buyer-dash.png'),
      externalLink: 'https://palette.joshgraham.io',
      images: [
        {
          src: require('@/assets/projects/palette/2fa-start.png'),
          caption: 'CloudOTP integration helps to make impersonating a person a hard attack to scale'
        },
        {
          src: require('@/assets/projects/palette/2fa-sent.png'),
          caption: ''
        },
        {
          src: require('@/assets/projects/palette/stripe.png'),
          caption: 'Stripe ID ensures that only real people can sell on Palette'
        },
        {
          src: require('@/assets/projects/palette/upload.png'),
          caption: 'The upload process creates a lot of metadata which then powers search'
        },
        {
          src: require('@/assets/projects/palette/listing.png'),
          caption: 'A simple page lets buyers inspect an item for sale'
        },
        {
          src: require('@/assets/projects/palette/cart.png'),
          caption: 'A common ecommerce step - reviewing your basket!'
        },
        {
          src: require('@/assets/projects/palette/checkout.png'),
          caption: 'Safe stripe checkout'
        }      ]
    },
    {
      title: 'Crescendo',
      slug: 'crescendo',
      date: 'April 2025',
      summary: 'AI generated Firebase app that allows students to find music teachers for online lessons',
      description: `
        <p>As a frustrated developer with some idea of how to write JS and PHP code, I wanted to see how good AI actually was. I set myself the challenge of writing a whole Vue and Firebase app using AI for every single line of code. The result? Crescendo.</p>

        <p>Features include:</p>
        <ul>
          <li>Teachers can set their weekly schedule</li>
          <li>Teachers can upload free resources, and also resources that they charge for</li>
          <li>Premium content can be made available to any student, or charged for as an asset</li>
        </ul>
      `,
      featuredImage: require('@/assets/projects/crescendo/hero.png'),
      externalLink: 'https://crescendo.joshgraham.io',
      images: [
        {
          src: require('@/assets/projects/crescendo/booklesson.png'),
          caption: 'Students can book lessons according to the schedule of the teacher'
        },
        {
          src: require('@/assets/projects/crescendo/editschedule.png'),
          caption: 'Teachers can update their weekly availability'
        },
        {
          src: require('@/assets/projects/crescendo/editresources.png'),
          caption: 'Teachers can upload teaching resources to their profile that are either free or charged for'
        },
        {
          src: require('@/assets/projects/crescendo/messages.png'),
          caption: 'Students and teachers can message each other, share documents, and book lessons from this page too once they pick a time'
        },
        {
          src: require('@/assets/projects/crescendo/onboarding.mov'),
          caption: 'Student onboarding asks for details about their learning wishes to match them with appropriate teachers'
        },
        {
          src: require('@/assets/projects/crescendo/past-lessons.png'),
          caption: 'Lesson management is essential and from here students can leave reviews, and handle any issues with their lesson'
        },
        {
          src: require('@/assets/projects/crescendo/review.png'),
          caption: 'Comprehensive reviews build a network effect across the platform'
        }
      ]
    },
    {
      title: 'Caseworker.app',
      slug: 'caseworker',
      date: '2021-2025',
      summary: 'Personally written Laravel app that allows UK elected representatives to manage casework from their constituents',
      description: `
        <p>I worked for an MP for years and I saw just how outdated the software that connects us to our representatives is. Wtih caseworker, I wanted to modernise the software that our representatives use to speak with us. Were I to pursue this further, I would try to integrate with various political party research teams.</p>

        <p>Features include:</p>
        <ul>
          <li>Manage emails via Microsoft Graph API</li>
          <li>Manage surgeries and calendar</li>
          <li>Tag all cases and contact constituents en masse</li>
        </ul>
      `,
      featuredImage: require('@/assets/projects/caseworker/outlook.png'),
      externalLink: 'https://casework.app',
      images: [
        {
          src: require('@/assets/projects/caseworker/outlook.png'),
          caption: 'Connect with your Outlook account and manage constituent emails in caseworker'
        },
        {
          src: require('@/assets/projects/caseworker/cases.png'),
          caption: 'Create cases from emails and keep them updated'
        },
        {
          src: require('@/assets/projects/caseworker/details.png'),
          caption: 'Update case details and add tags'
        },
        {
          src: require('@/assets/projects/caseworker/comments.png'),
          caption: 'Work with colleagues to stay up to date on developments in a case'
        },
        {
          src: require('@/assets/projects/caseworker/comms.png'),
          caption: 'Send emails and letters directly to constituents from caseworker'
        }
      ]
    },
    {
      title: 'MadeTech Voids',
      slug: 'voids-housing',
      date: 'April 2023',
      summary: "Zero to one B2B social housing project for MadeTech's software division",
      description: `
        <p>I led the development of Voids, an innovative B2B social housing lettings system designed to streamline the process of preparing properies for relet</p>

        <p>My responsibilities included:</p>
        <ul>
          <li>Leading a lean team through an experimental approach to create a "zero to one" product</li>
          <li>Conducting comprehensive user research with housing officers and tenants</li>
          <li>Establishing product strategy, roadmap, and go-to-market plans</li>
          <li>Representing the product at industry conferences and hosting panel discussion webinars</li>
          <li>Overseeing the entire product lifecycle from discovery to MVP delivery</li>
        </ul>

        <p>The system significantly reduces administrative overhead for housing providers, and allows us to house more people more efficiently by getting losing fewer days per property to relet work.</p>
      `,
      featuredImage: require('@/assets/projects/voids-housing/hero.png'),
      externalLink: 'https://voids.madetech.com/demo',
      images: [
        {
          src: require('@/assets/projects/voids-housing/hero.png'),
          caption: 'We tested several messages for the hero section and found that the reduction in workload burden was most compelling'
        },
        {
          src: require('@/assets/projects/voids-housing/cta.png'),
          caption: 'With local councils under such financial pressure, we knew that financial saving was a huge factor in decision making.'
        },
        {
          src: require('@/assets/projects/voids-housing/props.png'),
          caption: 'This demo, using fake properties and fake jobs generated by AI, demonstrated how our scheduler worked'
        },
        {
          src: require('@/assets/projects/voids-housing/results.png'),
          caption: 'After testing, we added as slight delay to results delivery. Our results were so much better that no one believed them without "thinking" time'
        }
      ]
    }
  ];

  // Helper function to get project by slug
  export function getProjectBySlug(slug) {
    return projectsData.find(project => project.slug === slug) || null;
  }
